import { css, html, LitElement, type TemplateResult } from "lit";
import { customElement, property } from "lit/decorators.js";
import { fireSpaNavigate } from "../../../services/router.ts";

const styles = css`
  :host {
    display: contents;
  }

  a {
    display: contents;
    color: inherit;
    text-decoration: none;
  }
`;

/**
 * `<dui-link>` — A layout-transparent SPA link.
 *
 * Renders a real `<a href>` inside shadow DOM for native link behaviors
 * (right-click menu, URL preview on hover, accessibility). On plain
 * left-click, dispatches a bubbling `spa-navigate` event instead of
 * performing a full page navigation. Modifier-clicks (cmd/ctrl/shift/middle)
 * fall through to the browser for new-tab behavior.
 *
 * The inner `<a>` uses `display: contents` so the host element controls
 * layout. Style the `<dui-link>` host directly (e.g. via a class from the
 * parent's CSS).
 *
 * @slot - Link content.
 */
@customElement("dui-link")
export class DuiLinkElement extends LitElement {
  static override styles = [styles];

  /** The URL to navigate to. */
  @property()
  accessor href = "";

  #onClick = (event: MouseEvent): void => {
    fireSpaNavigate(this, event, this.href);
  };

  override render(): TemplateResult {
    return html`<a href="${this.href}" @click="${this.#onClick}"
      ><slot></slot
    ></a>`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "dui-link": DuiLinkElement;
  }
}
